import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { DialogClass, DialogService, IDialogConfig } from '@services/dialog';
import { FeedbackService } from '@services/feedback/';
import { ProgressIndicatorService } from '@services/progress/';
import { IAvailableRoles, IManagedUser, IRole, IUserSearchFields, UsersService } from '@services/users';
import { finalize } from 'rxjs/operators';
import { environment } from '@env/environment';
import { I18nService, StaticService } from '@app/i18n/services';
import { ClustersService, ICountry, IGender } from '@services/clusters';

import {
	UsersAccountMigrationDialogComponent,
	UsersManualPasswordResetDialogComponent,
	UsersEditUserAttributesDialogComponent,
	UsersSendResetPasswordEmailDialogComponent,
	UsersSendVerificationEmailDialogComponent,
	UsersSuspendToggleDialogComponent,
	UsersTermsAcceptanceDialogComponent,
	UsersVerifyEmailDialogComponent,
	UsersManageRolesDialogComponent,
	UsersRolesSourcesDialogComponent
} from './components';

@Component({
	selector: 'app-users',
	templateUrl: './users.component.html',
	styleUrls: ['./users.component.scss']
})
export class UsersComponent implements OnInit, AfterViewInit {
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;
	@ViewChild(MatTable) table: MatTable<any>;

	public readonly environment = environment;

	public tableColumns: string[] = [
		'firstname',
		'lastname',
		'email',
		'validated',
		'suspended',
		'lastLogin',
		'createdTime',
		'uuid',
		'action'
	];
	public countries: ICountry[] = this.staticService.get('countries').map((country: { id: string; label: string; i18n: string }) => {
		return {
			id: country.id,
			label: this.i18nService.get(country.label, country.i18n)
		};
	});
	public genders: IGender[] = this.staticService.get('genders').map((gender: { id: string; label: string; i18n: string }) => {
		return {
			id: gender.id,
			label: this.i18nService.get(gender.label, gender.i18n)
		};
	});
	public roles: IRole[] = [];
	private availableRoles: IAvailableRoles = {
		canAdd: [],
		canRemove: []
	};

	public dialogClass = DialogClass;
	public searchFormValid = true;
	public openedMenu: number;
	public searchFormValues: IUserSearchFields = {
		query: {},
		ranges: {},
		createdTimezone: '',
		loginTimezone: '',
		emailVerified: 'all',
		gender: 'all',
		role: 'all',
		billingCountry: 'all'
	};

	constructor(
		private feedbackService: FeedbackService,
		private progressIndicatorService: ProgressIndicatorService,
		public usersService: UsersService,
		private dialogService: DialogService,
		private i18nService: I18nService,
		private staticService: StaticService,
		private clustersService: ClustersService
	) {}

	public ngOnInit(): void {
		this.progressIndicatorService.spinner(true);
		this.getGenders();
		this.getCountries();
		this.getRoles();
		this.getAvailableRoles();
		this.progressIndicatorService.spinner(false);
	}

	public ngAfterViewInit(): void {
		this.subscribeToTableChanges();
	}

	public search(force = true, resetPagination = false): void {
		this.getUsers(force, resetPagination);
	}

	public migrateUser(): void {
		const config: IDialogConfig = {
			data: {
				callback: () => {
					this.table.renderRows();
					this.getUsers(true);
				}
			}
		};

		this.dialogService.open(UsersAccountMigrationDialogComponent, config);
	}

	public getLocalizedDate(date: string | number, displayTime = true): string {
		if (displayTime) {
			return new Date(date).toLocaleString();
		}

		return new Date(date).toLocaleDateString();
	}

	public openActionDialog(user: IManagedUser, dialog: string, size = DialogClass.medium): void {
		const dialogs = {
			sendResetPasswordEmail: UsersSendResetPasswordEmailDialogComponent,
			sendVerificationEmail: UsersSendVerificationEmailDialogComponent,
			verifyEmail: UsersVerifyEmailDialogComponent,
			editUserAttributes: UsersEditUserAttributesDialogComponent,
			suspendToggle: UsersSuspendToggleDialogComponent,
			termsAcceptance: UsersTermsAcceptanceDialogComponent,
			setPassword: UsersManualPasswordResetDialogComponent,
			provisionRoles: UsersManageRolesDialogComponent,
			troubleshootRoles: UsersRolesSourcesDialogComponent
		};

		const data: any = {
			user,
			callback: (userDetails: IManagedUser, refreshSearch: boolean) => {
				this.table.renderRows();

				if (refreshSearch) {
					this.getUsers(true);
				}
			}
		};

		if (dialog === 'provisionRoles') {
			data.availableRoles = this.availableRoles;
		}

		this.dialogService.open(dialogs[dialog], {
			data,
			panelClass: size
		});
	}

	private getUsers(force = true, resetPagination = false): void {
		if (!this.searchFormValid) {
			this.progressIndicatorService.spinner(false);
			return;
		}

		this.progressIndicatorService.spinner(true);

		if (resetPagination) {
			this.usersService.clearCache();
		}

		this.usersService
			.getUsers(
				this.searchFormValues,
				this.paginator.pageSize,
				this.paginator.pageSize === this.usersService.userSearchQuery.pageSize && !resetPagination ? this.paginator.pageIndex : 0,
				this.sort.active,
				this.sort.direction,
				force
			)
			.pipe(finalize(() => this.progressIndicatorService.spinner(false)))
			.subscribe({
				error: (error) => this.feedbackService.apiError(error)
			});
	}

	private getGenders(): void {
		this.clustersService.getClusterGenders().subscribe((res: IGender[]) => {
			this.genders = res;
		});
	}

	private getCountries(): void {
		this.clustersService.getClusterCountries().subscribe((res: ICountry[]) => {
			this.countries = res;
		});
	}

	private getRoles(): void {
		this.clustersService.getRoles().subscribe((res: IRole[]) => {
			this.roles = res;
		});
	}

	private getAvailableRoles(): void {
		this.usersService.getAvailableRoles().subscribe({
			next: (res: IAvailableRoles) => {
				this.availableRoles = res;
			},
			error: (error: any) => this.feedbackService.apiError(error)
		});
	}
	private subscribeToTableChanges(): void {
		this.sort.sortChange.subscribe(() => {
			this.paginator.pageIndex = 0;
			this.getUsers();
		});
	}
}
